import React, { useState, useEffect } from 'react'
import KanbanView from '@/components/KanbanView'
import TableView from '@/components/TableView'
import { Columns3, Dot, TableProperties, X, Loader2 } from "lucide-react"
import api from '@/http/api'
import { Button } from "@univdiam/ui/components/button"
import { Badge } from "@univdiam/ui/components/badge"
import { Input } from "@univdiam/ui/components/input"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@univdiam/ui/components/dropdown-menu"

import { useOrder, useInfiniteOrder, useUpdateOrder } from '@/sharedData/Order'
import type { SortingState, VisibilityState } from '@univdiam/ui/lib/SharedTypes'
import { useTableDataColumn } from '@univdiam/ui/lib/TableDataColumn'
import { getColumns } from '@univdiam/ui/customComponents/OrderList/columns'
import type { OrderType } from '@/types'
import Loader from "@/components/Loader";
import EmptyScreen from '@univdiam/ui/customComponents/EmptyScreen'
import { useDashboardView, useOrderStore } from '@/store'
import { useNavigate } from 'react-router-dom'
import { ScrollArea } from '@univdiam/ui/components/scroll-area'
import { kanbanColumns } from "@univdiam/ui/lib/KanbanColumns";


// Per column, not per board. Ten fills a column on first paint; the rest
// arrives as the user scrolls that specific column.
const KANBAN_PAGE_SIZE = 10

const Dashbaord = () => {
  const navigate = useNavigate()
  const { setCreateOrder } = useOrderStore()
  const { view, setView } = useDashboardView()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState("")
  const [searchQuery, setSearchQuery] = useState("")

  const { data: tableData, isPending: isTablePending, isFetching: isTableFetching } = useOrder(searchQuery, page, 20, { enabled: view === 'Table' })
  const { mutate: updateOrder } = useUpdateOrder()
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})

  const initialVisibility: Record<string, boolean> = {}

  for (const col of kanbanColumns) {
    initialVisibility[col.id] = col.id !== 'cad_archive' && col.id !== 'completed_archive'
  }

  const [kanbanColumnVisibility, setKanbanColumnVisibility] = useState(initialVisibility)

  // One infinite query per Kanban column, so a column pages on its own cursor
  // rather than every column sharing one window over the whole order list --
  // which is what used to leave sparse statuses empty until you paged past
  // everything newer. kanbanColumns is a module-level constant, so this map
  // always calls the same hooks in the same order: never filter it here, gate
  // the fetch with `enabled` instead.
  const columnQueries = kanbanColumns.map((col) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useInfiniteOrder(searchQuery, KANBAN_PAGE_SIZE, col.id, {
      enabled: view === 'Kanban' && kanbanColumnVisibility[col.id],
    })
  )

  const kanbanOrders: OrderType[] = columnQueries.flatMap(
    (q) => q.data?.pages.flatMap((p: any) => p.data as OrderType[]) ?? []
  )
  const hasKanbanData = columnQueries.some((q) => !!q.data)
  // isLoading, not isPending: a disabled query stays pending forever.
  const isKanbanLoading = columnQueries.some((q) => q.isLoading)
  const isKanbanFetching = columnQueries.some((q) => q.isFetching)

  // fetchNextPage is referentially stable and the rest are primitives, so a
  // fresh wrapper object each render does not retrigger the column's observer.
  const columnPagination = Object.fromEntries(
    kanbanColumns.map((col, i) => [col.id, {
      fetchNextPage: columnQueries[i]!.fetchNextPage,
      hasNextPage: !!columnQueries[i]!.hasNextPage,
      isFetchingNextPage: columnQueries[i]!.isFetchingNextPage,
    }])
  )

  const isPending = view === 'Table' ? isTablePending : isKanbanLoading;
  const isFetching = view === 'Table' ? isTableFetching : isKanbanFetching;

  const newUpdateCount = view === 'Table'
    ? tableData?.data?.data?.filter((item: OrderType) => item.is_new_update).length || 0
    : kanbanOrders.filter((item: OrderType) => item.is_new_update).length;

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1)
      setSearchQuery(searchInput)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const handleSearch = () => {
    setPage(1)
    setSearchQuery(searchInput)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setPage(1)
      handleSearch()
    }
  }

  const handleClearSearch = () => {
    setSearchInput('')
    setSearchQuery('')
    setPage(1)
  }

  const handleViewChange = (value: 'Kanban' | 'Table') => {
    setView(value)
  }

  const handleOrderOpen = (id: string, customer_id: string) => {
    navigate(`/order/${id}/${customer_id}`)
  }

  const handleUpdateOrder = (order_id: string, customer_id: string, updates: { labels?: string[]; status?: string; is_new_update?: boolean; orderRef: string }) => {
    updateOrder({ order_id, customer_id, updates })
  }

  // Customers use open, free-text labels. The prefix-label vocabulary in
  // /admin/order-tags is admin-only and is not fetched here.
  const columns = getColumns(false, handleUpdateOrder, [], undefined)
  const table = useTableDataColumn(tableData?.data?.data || [], columns, setSorting, setColumnVisibility, "", sorting, columnVisibility)


  const hasData = view === 'Table' ? !!tableData : hasKanbanData
  if (isPending && !hasData) {
    return <Loader />
  }

  // Columns settle one at a time now, so "no orders" only holds once nothing is
  // still in flight -- otherwise the empty screen flashes mid-load.
  const isEmpty = view === 'Table'
    ? tableData?.data?.data?.length === 0
    : hasKanbanData && !isKanbanFetching && kanbanOrders.length === 0;

  if (isEmpty && !searchQuery) {
    return <EmptyScreen>
      <p className="flex items-center justify-center gap-1">Looks like you haven’t created any orders yet.<span className="font-semibold cursor-pointer underline" onClick={() => setCreateOrder(true)}> Start by creating one!</span></p>
    </EmptyScreen>
  }

  return (

    <div id="dashboard" className="p-4 md:p-[24px] min-h-[calc(100vh-60px)] md:min-h-screen w-full">
      <div className='bg-white rounded-lg border border-[#E5E5E5] overflow-hidden'>

        <div id='header' className="bg-white">
          <div className="p-4 md:px-5 md:py-3 border-b flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-0">
            {/* Title & Badge */}
            <div className="flex gap-2 justify-between lg:justify-start items-center w-full lg:w-auto">
              <p className="font-medium text-lg">My Orders</p>
              <Badge
                variant="outline"
                className="px-3 py-1 text-xs font-medium text-black border border-[#CFCFD1] rounded-sm"
              >
                <Dot size={6} strokeWidth={10} color='#00B878' className='rounded-full' />
                {newUpdateCount} new updates
              </Badge>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3 items-center w-full lg:w-auto">

              {/* View Toggle */}
              <div className="flex w-full sm:w-auto gap-1 p-1 bg-[#f6f6f6] rounded-md items-center justify-center sm:justify-start order-2 sm:order-1">
                {/* Kanban View Button */}
                <Columns3
                  className={`w-[28px] h-[28px] p-1.5 relative rounded-[2px] cursor-pointer transition-all duration-200
                    ${view === 'Kanban' ? 'bg-white shadow-sm border border-border ' : 'bg-transparent text-muted-foreground hover:text-foreground'}`}
                  size={16}
                  strokeWidth={1.5}
                  onClick={() => handleViewChange('Kanban')}
                />

                {/* Table View Button */}
                <TableProperties
                  className={`w-[28px] h-[28px] p-1.5 relative rounded-[2px] cursor-pointer transition-all duration-200
                    ${view === 'Table' ? 'bg-white shadow-sm border border-border ' : 'bg-transparent text-muted-foreground hover:text-foreground'}`}
                  size={16}
                  strokeWidth={1.5}
                  onClick={() => handleViewChange('Table')}
                />
              </div>

              {/* Search Bar */}
              <div className="relative w-full sm:w-auto order-1 sm:order-2 flex gap-2">
                <div className="relative w-full sm:w-64 lg:w-lg">
                  <Input
                    placeholder="Search Order"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full"
                  />
                  {
                    searchInput !== '' && <X className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 cursor-pointer" onClick={handleClearSearch} />
                  }
                </div>
                <Button type="button" variant="default" onClick={handleSearch} className="px-6 min-w-24 h-[34px]" disabled={isFetching}>
                  {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
                </Button>
              </div>

              {/* Columns */}
              <div className="w-full sm:w-auto order-3 sm:order-3 flex justify-end sm:justify-start">
                {view === 'Table' ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full sm:w-auto">
                        Columns
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {table
                        .getAllColumns()
                        .filter(
                          (column) => column.getCanHide()
                        )
                        .map((column) => {
                          return (
                            <DropdownMenuCheckboxItem
                              key={column.id}
                              className="capitalize"
                              checked={column.getIsVisible()}
                              onCheckedChange={(value) =>
                                column.toggleVisibility(!!value)
                              }
                            >
                              {column.id}
                            </DropdownMenuCheckboxItem>
                          )
                        })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full sm:w-auto">
                        Columns
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {kanbanColumns.map((col) => (
                        <DropdownMenuCheckboxItem
                          key={col.id}
                          className="capitalize"
                          checked={kanbanColumnVisibility[col.id]}
                          onCheckedChange={(value) =>
                            setKanbanColumnVisibility((prev) => ({
                              ...prev,
                              [col.id]: !!value,
                            }))
                          }
                        >
                          {col.title}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        </div>


        <div id='views'>
          <ScrollArea className="">
            {
              view == 'Kanban' ? <div className='p-[20px] overflow-x-scroll h-[calc(100vh-8rem)]'><KanbanView
                data={kanbanOrders}
                handleOrderOpen={handleOrderOpen}
                columnVisibility={kanbanColumnVisibility}
                globalFilter={""}
                isAdmin={false}
                columnPagination={columnPagination}
              /></div> :
                <div className='overflow-y-auto h-[calc(100vh-8rem)]'><TableView
                  data={tableData?.data?.data || []}
                  pagination={tableData?.data?.pagination}
                  page={page}
                  setPage={setPage}
                  isAdmin={false}
                  onUpdateOrder={handleUpdateOrder}
                  {...{ globalFilter: "", setGlobalFilter: () => { }, sorting, setSorting, columnVisibility, setColumnVisibility, handleOrderOpen }}
                /></div>
            }
          </ScrollArea>
        </div>
      </div>
    </div>

  )
}

export default Dashbaord